import { Button } from "@itandi/itandi-bb-ui/Button";
import { FloatingMenu } from "@itandi/itandi-bb-ui/FloatingMenu";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import Link from "next/link";
import { useRouter } from "next/router";
import type { ComponentProps } from "react";
import { useCallback, useState } from "react";
import useSWRMutation from "swr/mutation";
import { VendorDealLayout } from "~/components/common/layouts/deals/VendorDealLayout";
import Loading from "~/components/common/parts/loading/Loading";
import { ReportImagesArea } from "~/components/common/reports/ReportImagesArea";
import { ReportImagesAreaSp } from "~/components/common/reports/ReportImagesAreaSp";
import { ReportImageUploadPanel } from "~/components/common/reports/ReportImageUploadPanel";
import { useDealCompletionReportImages } from "~/hooks/vendors/deals/useDealCompletionReportImages";
import { useDeals } from "~/hooks/vendors/useDeals";
import { useSessions } from "~/hooks/vendors/useSessions";
import type { NskResponse } from "~/models/Api";
import type {
	DealCompletionReportImage,
	DealReportBulkUpdateParams,
	DealReportImage
} from "~/models/DealReportImage";
import { NUMBER_OF_DELETE_AT_ONCE } from "~/models/DealReportImage";
import { isSubVendor } from "~/models/DealVendorCompany";
import { PATH as DEALS_PATH } from "~/pages/vendors/deals";
import { PATH } from "~/pages/vendors/deals/[id]";
import { isSuccess } from "~/utils/api/api_handler";
import { getDownloadPath as getDownloadPdfPath } from "~/utils/api/vendors/deals/DealCompletionReport";
import {
	create as completionComplete,
	paths as completionPaths
} from "~/utils/api/vendors/deals/DealCompletionReportComplete";
import {
	bulkDelete,
	bulkUpdate,
	destroy,
	getDownloadPath as getDownloadImagePath,
	getDownloadZipPath,
	create as postCompletionReportImages,
	update,
	updateSeq
} from "~/utils/api/vendors/deals/DealCompletionReportImage";
import { uploadReportImages } from "~/utils/DealReportImageUtil";
import Styles from "./Page.module.scss";

export function Page() {
	const { notify } = useNotification();
	const router = useRouter();
	const id = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}

		return parsed;
	});
	const [uploading, setUploading] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [processedCount, setProcessedCount] = useState(0);
	const [totalCount, setTotalCount] = useState(0);
	const [selectedImageIds, setSelectedImageIds] = useState<readonly number[]>(
		[]
	);
	const { data: session } = useSessions().index();
	const { data: deal } = useDeals().show(id);
	// globalMutateしたい
	const { data: reportImages, mutate: mutateReportImages } =
		useDealCompletionReportImages().index(id);
	const vendorCompanyId = session?.vendorCompany.id ?? null;

	const { trigger, isMutating } = useSWRMutation(
		completionPaths.create(id),
		async (_key, { arg }: { arg: { dealId: number } }) =>
			completionComplete(arg.dealId)
	);

	const onComplete = useCallback(async () => {
		const result = await trigger({ dealId: id });
		if (isSuccess(result)) {
			await router.push(PATH(id));
			notify({ content: "完了しました。", variant: "success" });
		} else {
			notify({
				content: `エラーが発生しました。${result.message ?? ""}`,
				variant: "error"
			});
		}
	}, [trigger, id, router, notify]);

	if (deal === null || reportImages === null || vendorCompanyId === null) {
		return <Loading isLoading />;
	}
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: PATH(deal.id), label: "案件詳細" },
		{ label: "完了報告画像の登録" }
	] as const satisfies ComponentProps<typeof VendorDealLayout>["items"];
	const handleDrop = async (files: File[]): Promise<void> => {
		setProcessedCount(0);
		setTotalCount(files.length);
		setUploading(true);
		try {
			await uploadReportImages(files, {
				handleCreate: async (sendFiles) =>
					postCompletionReportImages(sendFiles, deal.id),
				onProgress: async (processedCount) => {
					setProcessedCount(processedCount);
					await mutateReportImages();
				}
			});
			notify({ content: "画像を登録しました。", variant: "success" });
		} catch {
			notify({
				content:
					"画像のアップロードに失敗しました。一覧に表示されていない画像を、もう一度アップロードしてください。",
				variant: "error"
			});
			// 失敗と判定された送信もサーバー側では登録済みの場合があるため、一覧を取り直してからアップロード欄を戻す
			await mutateReportImages();
		} finally {
			setUploading(false);
		}
	};
	const handleUpdate = async (
		submitValue: DealReportImage
	): Promise<NskResponse> => update(submitValue);
	const handleBulkUpdate = async (
		submitValue: DealReportBulkUpdateParams
	): Promise<void> => {
		const result = await bulkUpdate(deal.id, selectedImageIds, submitValue);
		if (isSuccess(result)) {
			await mutateReportImages();
			notify({ content: "更新しました。", variant: "success" });
		} else {
			notify({ content: "エラーが発生しました。", variant: "error" });
		}
	};
	const handleDestroy = async (
		reportImage: DealReportImage
	): Promise<NskResponse> => {
		const result = await destroy(reportImage);
		if (isSuccess(result)) {
			setSelectedImageIds((prev) => {
				const imageIndex = prev.indexOf(reportImage.id);

				return imageIndex === -1 ? prev : prev.toSpliced(imageIndex, 1);
			});
		}
		return result;
	};
	const handleBulkDelete = async (): Promise<void> => {
		if (selectedImageIds.length === 0) {
			return;
		}
		const total = selectedImageIds.length;
		setProcessedCount(0);
		setTotalCount(total);
		setDeleting(true);
		const imageIdArray: number[][] = [];
		let finishedFiles = 0;
		for (
			let i = 0, j = selectedImageIds.length;
			i < j;
			i += NUMBER_OF_DELETE_AT_ONCE
		) {
			imageIdArray.push(
				selectedImageIds.slice(i, i + NUMBER_OF_DELETE_AT_ONCE)
			);
		}
		for (const ids of imageIdArray) {
			const result = await bulkDelete(deal.id, ids);
			if (isSuccess(result)) {
				await mutateReportImages();
			} else {
				notify({ content: "エラーが発生しました。", variant: "error" });
				return;
			}
			finishedFiles += ids.length;
			setProcessedCount(finishedFiles);
			await mutateReportImages();
		}
		setDeleting(false);
		setSelectedImageIds([]);
		notify({ content: "画像を削除しました。", variant: "success" });
	};
	const handleUpdateSeq = async (
		newOrderItems: DealReportImage[]
	): Promise<void> => {
		await mutateReportImages(newOrderItems as DealCompletionReportImage[], {
			revalidate: false
		});
		await updateSeq(newOrderItems, id);
	};
	const handleDownloadPdf = (imagesPerPage: number): void => {
		const path = getDownloadPdfPath(deal.id, imagesPerPage, undefined);
		window.open(path);
	};
	const handleDownloadImage = (reportImage: DealReportImage): void => {
		const path = getDownloadImagePath(reportImage);
		window.open(path);
	};
	const handleDownloadZip = (): void => {
		const path = getDownloadZipPath(deal.id);
		window.open(path);
	};
	const handleSelectedDownloadZip = (): void => {
		const path = getDownloadZipPath(deal.id, selectedImageIds);
		window.open(path);
	};
	const handleSelectedDownloadPdf = (imagesPerPage: number): void => {
		const path = getDownloadPdfPath(deal.id, imagesPerPage, selectedImageIds);
		window.open(path);
	};
	const onChangeSelectedImage = (selectedImageId: number): void => {
		if (selectedImageIds.includes(selectedImageId)) {
			setSelectedImageIds(
				selectedImageIds.filter((imageId) => imageId !== selectedImageId)
			);
		} else {
			setSelectedImageIds(selectedImageIds.concat([selectedImageId]));
		}
	};
	const handleResetSelectedIds = (): void => {
		setSelectedImageIds([]);
	};
	const handleSelectAllImages = (): void => {
		setSelectedImageIds(reportImages.map((image: DealReportImage) => image.id));
	};
	return (
		<VendorDealLayout deal={deal} enableChat items={items} noBackground>
			<div className={Styles.MainWrapper}>
				<h2 className={Styles.ReportTitle}>完了報告画像の登録</h2>
				<ReportImageUploadPanel
					deleting={deleting}
					handleDrop={handleDrop}
					processedCount={processedCount}
					totalCount={totalCount}
					uploading={uploading}
				/>
				<ReportImagesArea
					companyType="vendor"
					handleBulkDelete={handleBulkDelete}
					handleBulkUpdate={handleBulkUpdate}
					handleDestroy={handleDestroy}
					handleDownloadImage={handleDownloadImage}
					handleDownloadPdf={handleDownloadPdf}
					handleDownloadZip={handleDownloadZip}
					handleResetSelectedIds={handleResetSelectedIds}
					handleSelectAllImages={handleSelectAllImages}
					handleSelectedDownloadPdf={handleSelectedDownloadPdf}
					handleSelectedDownloadZip={handleSelectedDownloadZip}
					handleUpdate={handleUpdate}
					handleUpdateSeq={handleUpdateSeq}
					onChangeSelectedImage={onChangeSelectedImage}
					reportImages={reportImages}
					selectedImageIds={selectedImageIds}
				/>
				<ReportImagesAreaSp
					companyType="vendor"
					handleBulkDelete={handleBulkDelete}
					handleBulkUpdate={handleBulkUpdate}
					handleDestroy={handleDestroy}
					handleDownloadImage={handleDownloadImage}
					handleDownloadPdf={handleDownloadPdf}
					handleDownloadZip={handleDownloadZip}
					handleResetSelectedIds={handleResetSelectedIds}
					handleSelectAllImages={handleSelectAllImages}
					handleSelectedDownloadPdf={handleSelectedDownloadPdf}
					handleSelectedDownloadZip={handleSelectedDownloadZip}
					handleUpdate={handleUpdate}
					handleUpdateSeq={handleUpdateSeq}
					onChangeSelectedImage={onChangeSelectedImage}
					reportImages={reportImages}
					selectedImageIds={selectedImageIds}
				/>
			</div>
			{selectedImageIds.length === 0 && (
				<FloatingMenu
					fitToParent
					rightArea={
						<Button loading={isMutating} onClick={onComplete} variant="primary">
							{isSubVendor(deal, vendorCompanyId)
								? "完了して通知"
								: "完了して戻る"}
						</Button>
					}
				/>
			)}
		</VendorDealLayout>
	);
}
